<template>
  <div class="no-shadow border rounded-sm">
    <h1>Lista de Oportunidades</h1>
    <b-card>
      <b-row class="mb-3" align-v="center">
        <b-col cols="12" class="d-inline-flex">
          <div class="col-9 px-0 d-inline-flex">
            <b-input-group class="search-field-list">
              <b-form-input
                v-model="termo_pesquisa"
                v-on:keyup.enter="searchTerm"
                placeholder="Pesquisar"
                class="search-field"
              />
              <b-input-group-append>
                <b-button @click="searchTerm" variant="exitos">
                  <font-awesome-icon icon="magnifying-glass" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <div class="px-2 text-secondary">
              <div class="vr" />
            </div>
            <b-button
              :variant="isVisibleFilters ? 'exitos' : 'secondary'"
              @click="toggleVisibleFilters"
            >
              <font-awesome-icon icon="sliders" class="mr-0" />
              <b-badge
                v-if="displayBadgeCount"
                class="position-absolute top-0 right-0 bg-danger rounded-pill badge-count-filters"
              >
                {{ countActiveAdvancedFilters }}
              </b-badge>
            </b-button>
          </div>
          <b-button
            variant="exitos"
            class="ml-auto"
            :to="{ name: 'oportunidades_create' }"
            v-if="$can('oportunidade:create')"
          >
            <font-awesome-icon icon="plus" />
            Nova Oportunidade
          </b-button>
        </b-col>
      </b-row>
      <div v-show="isVisibleFilters">
        <b-row>
          <b-col cols="12" class="px-3">
            <busca-avancada
              ref="busca-avancada"
              v-model="filters"
              :loading="isBusy"
              :total="pagination?.total"
              @search="searchData"
              @clear="clearFilters"
            />
          </b-col>
        </b-row>
      </div>
      <b-table
        striped
        hover
        :items="oportunidades_list"
        :fields="fields"
        :busy="isBusy"
        @sort-changed="sortingChanged"
        show-empty
        sort-icon-left
        class="list-oportunidades"
      >
        <template #cell(id)="{ item }">
          <div class="d-flex flex-column">
            <h5>#{{ item.id }}</h5>
            <span class="text-muted">{{ item.codigo }}</span>
          </div>
        </template>

        <template #cell(nome)="{ item }">
          <div class="d-flex flex-column">
            <router-link
              :to="{ name: 'oportunidades_edit', params: { id: item.id } }"
            >
              <h5>{{ item.nome }}</h5>
            </router-link>

            <span class="text-muted">{{ item.concedente_nome }}</span>
          </div>
        </template>

        <template #[`cell(tipos_concedentes.nome)`]="{ item }">
          <div>
            {{ item.origem }}
          </div>
          <hr class="my-2" />
          <div class="d-flex flex-column">
            <div
              class="d-inline-block text-truncate text-muted"
              style="max-width: 150px"
            >
              {{ item.categorias_nomes }}
            </div>
          </div>
        </template>

        <template #cell(rec_propostas)="{ item }">
          <div
            class="cell-rec_propostas"
            :class="{
              'cell-rec_propostas-sim': isDatePast(item.data_proposta_fim),
            }"
          >
            {{ item.data_proposta_fim | date_past }}
          </div>
        </template>

        <template #cell(valores)="{ item }">
          <div>
            <small class="d-block text-muted">Máx por projeto</small>
            <small class="text-uppercase">
              {{ item.moeda }}
            </small>
            {{ item.valor_projeto | number_format }}
          </div>
          <hr class="my-2" />
          <div>
            <small class="d-block text-muted">Total por Oportunidade</small>
            <small class="text-uppercase">
              {{ item.moeda }}
            </small>
            {{ item.valor_total | number_format }}
          </div>
        </template>

        <template #cell(data_proposta_fim)="{ item }">
          <div class="d-flex flex-column">
            <div v-show="item.data_proposta_fim">
              {{ item.data_proposta_fim | format_date }}
            </div>
            <div>{{ item.data_proposta_fim_tipo | tipo_proposta }}</div>
          </div>
        </template>

        <template #cell(updated_at)="{ item }">
          <div class="d-flex flex-column">
            <div>{{ item.updated_at | date_huminized }}</div>
            <small>{{ item.updated_at | format_datetime }}</small>
          </div>
        </template>

        <template #cell(actions)="row">
          <Actions
            :item="row.item"
            :view_show="false"
            :view_edit="$can('oportunidade:edit')"
            :view_delete="$can('oportunidade:delete')"
            edit_to="oportunidades_edit"
            @delete="deleteItem"
          />
        </template>

        <template #empty="{}">
          <h5 class="text-center">Nenhum item encontado</h5>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </b-card>

    <div>
      <pagination-nav :pagination="pagination" @input="loadData()" />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import moment from "moment";
import mixins from "@/components/Table/mixins";
import BuscaAvancada from "@/modules/oportunidades/Components/BuscaAvancada.vue";
import FiltersList from "@/modules/oportunidades/Components/FiltersList.vue";
import { isEqual } from "lodash";
import {
  convertQuery,
  convertFormToApi,
} from "@/helpers/functions/oportunidade_query";
import { toggleValue } from "@/mixins/toggle";
import {
  filterGroups,
  formEmptyDefault,
} from "@/modules/oportunidades/constants/formEmptyDefault";
import { deepCopy, merge } from "@/helpers/functions/ObjectHelpers";
import { isSafeArray, safeArray, size } from "@/helpers/functions/ArrayHelpers";

export default {
  name: "ListOportunidades",
  components: {
    BuscaAvancada,
    FiltersList,
  },
  mixins: [mixins, toggleValue("visible filters")],
  data: () => ({
    filters: formEmptyDefault(),
  }),
  computed: {
    ...mapState("oportunidades", [
      "oportunidades_list",
      "oportunidades",
      "pagination",
    ]),
    ...mapGetters("oportunidades", ["qualificacao_proposta"]),
    fields() {
      return [
        { key: "id", label: "COD", class: "col-1", sortable: true },
        { key: "nome", label: "nome", class: "text-wrap", sortable: true },
        {
          key: "tipos_concedentes.nome",
          label: "Origem / Categoria",
          class: "col-2",
          sortable: true,
          sortKey: "tipos_concedentes.nome",
        },
        { key: "valores", label: "Valores", class: "col-2" },
        { key: "rec_propostas", label: "Recebendo Propostas", class: "col-1" },
        {
          key: "data_proposta_fim",
          label: "Prazo",
          class: "col-1",
          sortable: true,
        },
        {
          key: "updated_at",
          label: "Última atualização",
          class: "col-1",
          sortable: true,
        },
        { key: "actions", label: "", class: "cell-actions" },
      ];
    },
    countActiveAdvancedFilters() {
      const activeFilters = deepCopy(this.filters);
      const sourceQuery = convertFormToApi(activeFilters);
      return safeArray(filterGroups.all)
        .filter((key) => {
          if (key === "page") return false;

          const source = Reflect.get(sourceQuery, key);
          if (isSafeArray(source)) return size(source) > 0;

          return !!source;
        })
        .reduce((acc) => (acc += 1), 0);
    },
    displayBadgeCount() {
      return this.countActiveAdvancedFilters > 0 && !this.isVisibleFilters;
    },
  },
  methods: {
    ...mapActions("oportunidades", [
      "ActionGetAll",
      "ActionList",
      "ActionDelete",
    ]),
    ...mapActions("moedas", { getAllMoedas: "ActionToSelect" }),
    isDatePast(value) {
      if (!value) return true;

      const isPast = moment(value, ["YYYY-MM-DD"]).isAfter(moment(), "days");
      return isPast;
    },
    getMoedaById(id) {
      const selected = find(this.moedas, { value: id });
      return selected ? selected.simbolo : "R$";
    },
    async searchData() {
      await this.loadData();
    },
    async clearFilters() {
      this.filters = formEmptyDefault();
      await this.loadData();
    },
    async loadData() {
      this.isBusy = true;
      const activeFilters = deepCopy(this.filters);
      const sourceQuery = convertFormToApi(activeFilters);

      try {
        const payload = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          orderBy: this.sortBy,
          sortBy: this.sortDesc ? "desc" : "asc",
          q: this.termo_pesquisa,
          ...sourceQuery,
        };

        await this.$router.push({ query: sourceQuery }).catch((err) => void 0);

        if (isEqual(this._last_request, payload)) return;

        this._last_request = payload;

        await this.ActionList(payload);
      } catch (error) {
        this.$notifications.addNotification({
          message: "Erro ao carregar dados da listagem",
          icon: "exclamation-triangle",
          type: "danger",
        });
      } finally {
        this.isBusy = false;
      }
    },
  },
  filters: {
    date_huminized: function (value) {
      if (!value) {
        return "Indeterminado";
      }
      return moment(value).fromNow();
    },
    date_past: function (value) {
      if (!value) {
        return "Sim";
      }
      const diff_days = moment(value, ["YYYY-MM-DD"]).isAfter(moment(), "days");
      return diff_days ? "Sim" : "Não";
    },
    tipo_proposta: (value) => {
      const tipos = {
        determinada: "Determinada",
        indeterminada: "Indeterminada",
        continua: "Contínua",
      };
      return tipos[value] ? tipos[value] : "Indeterminada";
    },
  },
  async created() {
    const query = this.$route.query;
    const result = await convertQuery(query);
    this.filters = merge(formEmptyDefault(), result);
    this.loadData();
  },
};
</script>

<style scoped lang="scss">
.vr {
  position: relative;
  width: 1px;
  height: 100%;
  background-color: currentColor;
}

.max-h-filters {
  max-height: 500px;
  overflow: hidden auto;
}

.badge-count-filters {
  top: -0.5rem;
  right: -0.5rem;
}

::v-deep .list-oportunidades {
  tr {
    th {
      white-space: normal !important;
    }
  }

  .cell-rec_propostas {
    font-weight: bold;
    border: 1px solid #ddd;
    padding: 0.3rem;
    text-align: center;
    color: #f5365c;
    text-transform: uppercase;

    &.cell-rec_propostas-sim {
      color: #2dce89;
    }
  }
}
</style>
